import { useEffect, useRef, useState } from 'react';
import { api, ApiError, uploadToPresignedUrl } from '../../lib/api';
import { useSession } from '../../lib/store';
import { Card, Empty, Notice, PageHeading, Spinner } from '../../components/Shell';
import { Stamp } from '../../components/Stamp';
import { SelectField, TextArea, TextField } from '../../components/Field';
import { dateRange, longDate } from '../../lib/format';
import type { Booking, ClientProfile, Pet, TrainingVideo, VideoAudience } from '../../lib/types';

const ACCEPTED = 'video/mp4,video/quicktime,video/webm,video/x-m4v';
const MAX_BYTES = 1024 * 1024 * 1024;

type AudienceKind = VideoAudience['kind'];

const KIND_LABELS: Record<AudienceKind, string> = {
  clients: 'Every client',
  client: 'One client',
  pet: 'One pet',
  booking: 'One booking',
};

/**
 * Video sharing, staff side: upload, choose who can watch, and revoke by
 * deleting. A clip may be a training lesson or just a pet having a good time;
 * either way this replaces an unlisted YouTube link with a file under the
 * business's own storage that is never public and is only ever watched
 * through a link the API mints per request.
 */
export function AdminVideos() {
  const session = useSession();
  const tenantId = session.tenant?.tenantId;
  const fileInput = useRef<HTMLInputElement>(null);

  const [videos, setVideos] = useState<TrainingVideo[]>([]);
  const [clients, setClients] = useState<ClientProfile[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [audienceKind, setAudienceKind] = useState<AudienceKind>('clients');
  const [audienceClientId, setAudienceClientId] = useState('');
  const [audiencePetId, setAudiencePetId] = useState('');
  const [audienceBookingId, setAudienceBookingId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const refresh = () => {
    if (!tenantId) return Promise.resolve();
    return api<{ videos: TrainingVideo[] }>(`/tenants/${tenantId}/videos`)
      .then((result) => {
        setVideos(result.videos);
        setError(null);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Videos could not be loaded.'));
  };

  useEffect(() => {
    if (!tenantId) return;
    void Promise.all([
      refresh(),
      api<{ clients: ClientProfile[] }>(`/tenants/${tenantId}/clients`).then((r) => setClients(r.clients)),
      api<{ pets: Pet[] }>(`/tenants/${tenantId}/pets`).then((r) => setPets(r.pets)),
      api<{ bookings: Booking[] }>(`/tenants/${tenantId}/bookings`).then((r) => setBookings(r.bookings)),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const clientName = (clientId: string) => clients.find((c) => c.clientId === clientId)?.fullName ?? 'A client';
  const petName = (petId: string) => pets.find((p) => p.petId === petId)?.name ?? 'A pet';
  const bookingLabel = (bookingId: string) => {
    const booking = bookings.find((b) => b.bookingId === bookingId);
    if (!booking) return 'A booking';
    return `${clientName(booking.clientId)}, ${dateRange(booking.startDate, booking.endDate)}`;
  };

  const audienceSummary = (audience: VideoAudience): string => {
    switch (audience.kind) {
      case 'clients':
        return 'Every client';
      case 'client':
        return clientName(audience.clientId);
      case 'pet':
        return `${petName(audience.petId)}’s owner`;
      case 'booking':
        return bookingLabel(audience.bookingId);
    }
  };

  const chooseFile = (file: File | null) => {
    setFieldErrors({});
    if (!file) {
      setPendingFile(null);
      return;
    }
    if (file.size > MAX_BYTES) {
      setFieldErrors({ file: 'That file is over 1GB. Trim the clip and try again.' });
      setPendingFile(null);
      return;
    }
    setPendingFile(file);
    if (!title) setTitle(file.name.replace(/\.[^.]+$/, '').slice(0, 60));
  };

  const buildAudience = (): VideoAudience | null => {
    if (audienceKind === 'clients') return { kind: 'clients' };
    if (audienceKind === 'client') return audienceClientId ? { kind: 'client', clientId: audienceClientId } : null;
    if (audienceKind === 'pet') return audiencePetId ? { kind: 'pet', petId: audiencePetId } : null;
    return audienceBookingId ? { kind: 'booking', bookingId: audienceBookingId } : null;
  };

  const upload = (event: React.FormEvent) => {
    event.preventDefault();
    if (!tenantId || !pendingFile) return;

    const audience = buildAudience();
    if (!audience) {
      setFieldErrors({ audience: 'Choose who this video is for.' });
      return;
    }

    setUploading(true);
    setFieldErrors({});

    void (async () => {
      try {
        const contentType = pendingFile.type || 'video/mp4';

        const ticket = await api<{ video: TrainingVideo; uploadUrl: string }>(
          `/tenants/${tenantId}/videos/upload-url`,
          {
            method: 'POST',
            body: {
              title,
              description: description || undefined,
              fileName: pendingFile.name,
              contentType,
              sizeBytes: pendingFile.size,
              audience,
            },
          }
        );

        await uploadToPresignedUrl(ticket.uploadUrl, pendingFile, contentType);

        await api(`/tenants/${tenantId}/videos/${ticket.video.videoId}/confirm`, { method: 'POST' });

        setPendingFile(null);
        setTitle('');
        setDescription('');
        setAudienceKind('clients');
        setAudienceClientId('');
        setAudiencePetId('');
        setAudienceBookingId('');
        if (fileInput.current) fileInput.current.value = '';
        await refresh();
      } catch (err) {
        if (err instanceof ApiError) {
          setFieldErrors(err.fieldErrors ?? {});
          if (!err.fieldErrors) setError(err.message);
        } else {
          setError('The upload did not finish. Try again.');
        }
      } finally {
        setUploading(false);
      }
    })();
  };

  const remove = (videoId: string) => {
    if (!tenantId) return;
    void api(`/tenants/${tenantId}/videos/${videoId}`, { method: 'DELETE' })
      .then(refresh)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That video could not be removed.'));
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-5 py-16">
        <Spinner label="Loading videos" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-5 pb-24 pt-12">
      <PageHeading eyebrow={session.tenant?.businessName} title="Videos" />

      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      <Card className="p-6">
        <form onSubmit={upload} className="space-y-5">
          <div>
            <span className="field-label">Choose a video</span>
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPTED}
              onChange={(e) => chooseFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm file:mr-3 file:cursor-pointer file:rounded-sm file:border file:border-[color:var(--color-ink)] file:bg-transparent file:px-3 file:py-1.5 file:font-sans file:text-sm file:font-bold"
            />
            {fieldErrors.file && <span className="field-error">{fieldErrors.file}</span>}
          </div>

          {pendingFile && (
            <>
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField
                  label="Title"
                  value={title}
                  onChange={setTitle}
                  error={fieldErrors.title}
                  required
                />
                <SelectField
                  label="Share with"
                  value={audienceKind}
                  onChange={(value) => setAudienceKind(value as AudienceKind)}
                  options={Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label }))}
                  error={fieldErrors.audience}
                />
              </div>

              {audienceKind === 'client' && (
                <SelectField
                  label="Client"
                  value={audienceClientId}
                  onChange={setAudienceClientId}
                  options={[
                    { value: '', label: 'Choose a client' },
                    ...clients.map((c) => ({ value: c.clientId, label: c.fullName })),
                  ]}
                />
              )}
              {audienceKind === 'pet' && (
                <SelectField
                  label="Pet"
                  value={audiencePetId}
                  onChange={setAudiencePetId}
                  options={[
                    { value: '', label: 'Choose a pet' },
                    ...pets.map((p) => ({ value: p.petId, label: `${p.name} (${clientName(p.clientId)})` })),
                  ]}
                />
              )}
              {audienceKind === 'booking' && (
                <SelectField
                  label="Booking"
                  value={audienceBookingId}
                  onChange={setAudienceBookingId}
                  options={[
                    { value: '', label: 'Choose a booking' },
                    ...bookings.map((b) => ({ value: b.bookingId, label: bookingLabel(b.bookingId) })),
                  ]}
                />
              )}

              <TextArea
                label="Description"
                value={description}
                onChange={setDescription}
                rows={2}
                placeholder="What the client is watching, or what to practise before the next visit."
              />

              <button type="submit" className="btn btn-primary" disabled={uploading || !title}>
                {uploading ? 'Uploading' : 'Share video'}
              </button>
            </>
          )}
        </form>
      </Card>

      <div className="mt-8">
        {videos.length === 0 ? (
          <Empty
            title="No videos yet"
            body="Upload a clip above and choose who it is for. One family, one pet, one booking, or everybody."
          />
        ) : (
          <ul className="space-y-2">
            {videos.map((video) => (
              <li key={video.videoId} className="card flex flex-wrap items-center justify-between gap-4 p-4">
                <div className="min-w-0">
                  <p className="min-w-0 truncate font-semibold">{video.title}</p>
                  <p className="typed text-xs text-[color:var(--color-ink-soft)]">
                    {audienceSummary(video.audience)} &middot; added {longDate(video.createdAt)}
                  </p>
                  {video.description && (
                    <p className="mt-1 min-w-0 text-sm text-[color:var(--color-ink-soft)]">{video.description}</p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-3">
                  {video.confirmedAt ? (
                    <Stamp tone="ok" flat>
                      Ready
                    </Stamp>
                  ) : (
                    <Stamp tone="pending" flat>
                      Uploading
                    </Stamp>
                  )}
                  <button type="button" className="btn btn-quiet" onClick={() => remove(video.videoId)}>
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
